/**
 * Trajectory file format v2 (Sprint 3, D5): integers in a JSON envelope — time in seconds since J2000 TDB, position
 * in metres, velocity in mm/s. Each value is stored as the difference to a prediction from the samples before it,
 * so the numbers stay short and gzip compresses them well:
 *
 * - time: the previous time (a plain delta);
 * - velocity: linear extrapolation of the two previous velocities over the time step (a plain delta for sample 1);
 * - position: the previous position advanced by the mean of the previous and the current velocity (trapezoid rule).
 *
 * Predictions are made from the quantised integers on both sides, so decoding is exact. JSON integers are exact up to
 * 2⁵³ (positions up to ~9·10¹⁵ m ≈ 60,000 AU). The decoder returns the in-memory form (days, km, km/day) that
 * `Trajectory` and `BodyTable` interpolate. (Predictive deltas instead of plain ones cut Cassini's file by ~15 %.)
 */
import type { EncodedSegment, TrajectoryData, TrajectoryFile, TrajectorySegmentData } from './data';

export const SECONDS_PER_DAY = 86_400;
/** km/day per mm/s. */
const KM_PER_DAY_PER_MM_S = SECONDS_PER_DAY / 1e6;

export interface DecodedSegment extends TrajectorySegmentData {
  t: Float64Array;
  s: Float64Array;
}

export function decodeSegment(seg: EncodedSegment): DecodedSegment {
  const n = seg.t.length;
  if (seg.s.length !== 6 * n)
    throw new Error(`segment ${seg.center}: expected ${6 * n} state values, got ${seg.s.length}`);
  const t = new Float64Array(n);
  const s = new Float64Array(6 * n);
  const T = new Float64Array(n);
  const q = new Float64Array(6 * n); // quantised integers: m and mm/s
  for (let i = 0; i < n; i++) {
    T[i] = (i > 0 ? T[i - 1]! : 0) + seg.t[i]!;
    for (let c = 0; c < 3; c++) {
      const v = 6 * i + 3 + c;
      q[v] = predictVelocity(q, T, i, c) + seg.s[v]!;
      const p = 6 * i + c;
      q[p] = predictPosition(q, T, i, c) + seg.s[p]!;
    }
    t[i] = T[i]! / SECONDS_PER_DAY;
    for (let c = 0; c < 3; c++) {
      s[6 * i + c] = q[6 * i + c]! / 1000;
      s[6 * i + 3 + c] = q[6 * i + 3 + c]! * KM_PER_DAY_PER_MM_S;
    }
  }
  const out: DecodedSegment = { center: seg.center, kind: seg.kind, t, s };
  if (seg.inner) out.inner = seg.inner;
  return out;
}

/** Predicted velocity component `c` (mm/s) of sample `i` from the quantised samples before it. */
function predictVelocity(q: ArrayLike<number>, T: ArrayLike<number>, i: number, c: number): number {
  if (i === 0) return 0;
  const v1 = q[6 * (i - 1) + 3 + c]!;
  if (i === 1) return v1;
  const v0 = q[6 * (i - 2) + 3 + c]!;
  return Math.round(v1 + ((v1 - v0) * (T[i]! - T[i - 1]!)) / (T[i - 1]! - T[i - 2]!));
}

/** Predicted position component `c` (m) of sample `i`: trapezoid rule with the previous and the current velocity. */
function predictPosition(q: ArrayLike<number>, T: ArrayLike<number>, i: number, c: number): number {
  if (i === 0) return 0;
  const vMean = (q[6 * (i - 1) + 3 + c]! + q[6 * i + 3 + c]!) / 2;
  return Math.round(q[6 * (i - 1) + c]! + (vMean * (T[i]! - T[i - 1]!)) / 1000);
}

/** Encodes decoded samples (days, km, km/day) into format v2. Inverse of `decodeSegment` to < 1 m and < 1 mm/s. */
export function encodeSegment(seg: TrajectorySegmentData): EncodedSegment {
  const n = seg.t.length;
  const T = new Float64Array(n);
  const q = new Float64Array(6 * n);
  for (let i = 0; i < n; i++) {
    T[i] = Math.round(seg.t[i]! * SECONDS_PER_DAY);
    for (let c = 0; c < 3; c++) {
      q[6 * i + c] = Math.round(seg.s[6 * i + c]! * 1000);
      q[6 * i + 3 + c] = Math.round(seg.s[6 * i + 3 + c]! / KM_PER_DAY_PER_MM_S);
    }
  }
  const t: number[] = new Array<number>(n);
  const s: number[] = new Array<number>(6 * n);
  for (let i = 0; i < n; i++) {
    t[i] = T[i]! - (i > 0 ? T[i - 1]! : 0);
    for (let c = 0; c < 3; c++) {
      s[6 * i + 3 + c] = q[6 * i + 3 + c]! - predictVelocity(q, T, i, c);
      s[6 * i + c] = q[6 * i + c]! - predictPosition(q, T, i, c);
    }
  }
  const out: EncodedSegment = { center: seg.center, kind: seg.kind, t, s };
  if (seg.inner) out.inner = [Math.round(seg.inner[0] * 1e6) / 1e6, Math.round(seg.inner[1] * 1e6) / 1e6];
  return out;
}

/** Decodes a trajectory file (format v2) into the in-memory form. */
export function decodeTrajectory(file: TrajectoryFile): TrajectoryData {
  if (file.format !== 2) throw new Error(`${file.spacecraft}: unsupported trajectory format ${String(file.format)}`);
  return { spacecraft: file.spacecraft, source: file.source, segments: file.segments.map(decodeSegment) };
}
